import { Injectable } from '@angular/core'
import { Store } from '@ngrx/store'
import {
  ContactInfoField,
  ContactInfoFieldScope,
  ContactInfoFieldValueType,
} from 'models/contact-info-fields'
import { HubtypeBot } from 'models/hubtype-bot'
import { HubtypeFolder } from 'models/hubtype-folder'
import { HubtypeProject } from 'models/hubtype-project'
import { HubtypeTemplate } from 'models/hubtype-template'
import { HubtypeUser } from 'models/hubtype-user'
import { Observable, forkJoin, of } from 'rxjs'
import { first, map, switchMap } from 'rxjs/operators'
import * as organizationActions from '../../actions/organization'
import { FeatureFlag } from '../../constants/feature-flags'
import {
  HubtypeOrganization,
  HubtypeOrganizationComplete,
  MyOrganization,
} from '../../models/hubtype-organization'
import * as fromRoot from '../../reducers'
import { ConverterService } from '../converter.service'
import { ContactInfoFieldFilter } from './../../models/contact-info-fields'
import { BotService } from './bot.service'
import { HubtypeApiService } from './hubtype-api.service'
import { ProjectService } from './project.service'
import { TemplateService } from './template.service'
import { UserService } from './user.service'

type OrganizationFieldsResponses = [
  MyOrganization,
  HubtypeTemplate[],
  HubtypeFolder[],
  HubtypeBot[],
  HubtypeUser[],
  HubtypeProject[],
]
@Injectable({ providedIn: 'root' })
export class OrganizationService {
  constructor(
    protected store: Store<fromRoot.State>,
    private apiService: HubtypeApiService,
    private userService: UserService,
    private convertService: ConverterService,
    private templateService: TemplateService,
    private botService: BotService,
    private projectService: ProjectService
  ) {}

  getMyOrganization(): Observable<MyOrganization> {
    return this.apiService
      .get(`/my-organization/`, null, 'v1')
      .pipe(
        map(response =>
          this.convertService.jsonConvert.deserializeObject(
            response,
            MyOrganization
          )
        )
      )
  }

  getAndAssembleHubtypeOrganization(): Observable<HubtypeOrganizationComplete> {
    const parallelRequests = [
      this.getMyOrganization(),
      this.templateService.getAllpaginatedTemplates(),
      this.templateService.getAllpaginatedTemplateFolders(),
      this.botService.getAllpaginatedBots(),
      this.userService.getAllPaginatedUsers(),
      this.projectService.getAllPaginatedProjects(),
    ]
    return forkJoin(parallelRequests).pipe(
      first(),
      map((responses: OrganizationFieldsResponses) =>
        this.assembleOrganization(responses)
      )
    )
  }

  hasFeatureFlag(flag: FeatureFlag): Observable<boolean> {
    return this.store.select(fromRoot.getOrganization).pipe(
      map(org => {
        if (!org || !org.feature_flags_json) {
          return false
        }
        return Boolean(org.feature_flags_json[flag])
      })
    )
  }

  hasLlmEnabledForTranslations(): Observable<boolean> {
    return this.store
      .select(fromRoot.getOrganization)
      .pipe(map(org => org?.settings?.isTranslationClientChatGTP))
  }

  updateWebhookSettings(
    organization: HubtypeOrganization
  ): Observable<MyOrganization> {
    return this.apiService
      .patch(
        '/my-organization/',
        {
          webhook_url: organization.webhook_url,
          subscribe_case_received: organization.subscribe_case_received,
          subscribe_case_newmessage: organization.subscribe_case_newmessage,
          subscribe_case_resolved: organization.subscribe_case_resolved,
          subscribe_case_assigned: organization.subscribe_case_assigned,
          subscribe_case_transferred: organization.subscribe_case_transferred,
        },
        'v1'
      )
      .pipe(
        map(response =>
          this.convertService.jsonConvert.deserializeObject(
            response,
            MyOrganization
          )
        )
      )
  }

  updateName(organizationName: string): Observable<MyOrganization> {
    return this.apiService
      .patch(`/my-organization/`, { name: organizationName }, 'v1', false)
      .pipe(
        switchMap((org: any) => {
          this.store.dispatch(
            new organizationActions.UpdateNameAction(org.name)
          )
          return of(org)
        }),
        map(org =>
          this.convertService.jsonConvert.deserializeObject(org, MyOrganization)
        )
      )
  }

  testWebhook(organization: HubtypeOrganization) {
    if (!organization) {
      return
    }

    const body = { webhook_url: organization.webhook_url }
    return this.apiService.post(
      `/organizations/${organization.id}/test_webhook/`,
      body,
      {},
      'v2'
    )
  }

  getContactInfoFields(params?: ContactInfoFieldFilter): Observable<{
    count: number
    fields: ContactInfoField[]
  }> {
    return this.apiService
      .get(`/contact-info-fields/?order=-created_at&is_active=true`, params)
      .pipe(
        first(),
        map(field => ({
          count: field.count,
          fields: this.convertService.jsonConvert.deserializeArray(
            field.results,
            ContactInfoField
          ),
        }))
      )
  }

  createContactInfoField(
    name: string,
    valueType: ContactInfoFieldValueType,
    scope: ContactInfoFieldScope,
    description: string,
    allowLlmDataCapture: boolean
  ): Observable<ContactInfoField> {
    return this.apiService
      .post(
        `/contact-info-fields/`,
        {
          name,
          value_type: valueType,
          scope,
          description,
          allow_llm_data_capture: allowLlmDataCapture,
        },
        {},
        'v1',
        false
      )
      .pipe(
        map(json =>
          this.convertService.jsonConvert.deserializeObject(
            json,
            ContactInfoField
          )
        )
      )
  }

  updateContactInfoField(
    fieldId: string,
    name: string,
    description: string,
    allowLlmDataCapture: boolean
  ): Observable<ContactInfoField> {
    return this.apiService
      .patch(
        `/contact-info-fields/${fieldId}/`,
        {
          name,
          description,
          allow_llm_data_capture: allowLlmDataCapture,
        },
        'v1',
        false
      )
      .pipe(
        map(json =>
          this.convertService.jsonConvert.deserializeObject(
            json,
            ContactInfoField
          )
        )
      )
  }

  deleteContactInfoField(fieldId: string) {
    return this.apiService
      .delete(`/contact-info-fields/${fieldId}/`)
      .pipe(first())
  }

  private assembleOrganization(
    pieces: OrganizationFieldsResponses
  ): HubtypeOrganizationComplete {
    const [myOrg, templates, templateFolders, bots, users, projects] = pieces
    const organization = myOrg.toOrganizationComplete()
    organization.templates = templates
    organization.template_folders = templateFolders
    organization.bots = bots
    organization.users = users
    organization.projects = projects
    return organization
  }
}
